// src/components/myAccount/MyAccountApp.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/authService';
import { fetchUserRoles } from '../../services/subscriptionApi';
import { supabase } from '../../config';
import MyAccountSidebar from './MyAccountSidebar';
import MyAccountHeader from './MyAccountHeader';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import LoginDetailsPage from './pages/LoginDetailsPage';
import EnhancedSubscriptionsPage from './pages/EnhancedSubscriptionsPage';
import EnhancedPaymentMethodsPage from './pages/EnhancedPaymentMethodsPage';
import BillingHistoryPage from './pages/BillingHistoryPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';

const MyAccountApp = () => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from database
  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error fetching user profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeMyAccount = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          
          // Fetch user profile from database
          const profile = await fetchUserProfile(currentUser.id);
          setUserProfile(profile);
          
          // Fetch user roles/subscriptions
          const roles = await fetchUserRoles();
          setUserRoles(roles);
        }
      } catch (error) {
        console.error('Error initializing My Account:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeMyAccount();
  }, []);

  const refreshUserData = async () => {
    try {
      const roles = await fetchUserRoles();
      setUserRoles(roles);
      
      // Refresh user profile
      if (user?.id) {
        const profile = await fetchUserProfile(user.id);
        setUserProfile(profile);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar */}
        <MyAccountSidebar userProfile={userProfile} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <MyAccountHeader user={user} userProfile={userProfile} userRole={null} />
        
        {/* Content Area */}
        <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route 
              path="/" 
              element={<Navigate to="/my-account/dashboard" replace />} 
            />
            <Route 
              path="/dashboard" 
              element={
                <DashboardPage 
                  user={user} 
                  userProfile={userProfile}
                  userRoles={userRoles}
                  onRefreshData={refreshUserData}
                />
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProfilePage 
                  user={user} 
                  userProfile={userProfile}
                  onRefreshData={refreshUserData}
                />
              } 
            />
            <Route 
              path="/login-details" 
              element={
                <LoginDetailsPage 
                  user={user}
                />
              } 
            />
            <Route 
              path="/subscriptions" 
              element={
                <EnhancedSubscriptionsPage 
                  user={user}
                  userRoles={userRoles}
                  onRefreshData={refreshUserData}
                />
              } 
            />
            <Route 
              path="/payment-methods" 
              element={
                <EnhancedPaymentMethodsPage 
                  user={user}
                />
              } 
            />
            <Route 
              path="/billing-history" 
              element={
                <BillingHistoryPage 
                  user={user}
                  userRoles={userRoles}
                  onRefreshData={refreshUserData}
                />
              } 
            />
            <Route 
              path="/subscription-success" 
              element={<SubscriptionSuccessPage />} 
            />
            <Route path="*" element={<Navigate to="/my-account/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default MyAccountApp;